import React from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useSocialPublishingAvailable } from './hooks/useSocialPublishingAvailable';
import { isAdminHost, STUDIO_ENABLED } from './config';
import Seo from './seo/Seo';
import { resolveSeo } from './seo/seoConfig';
import type { SeoConfig } from './seo/seoConfig';
import { useCasePages } from './components/Mainpage/useCaseContent';

// Lazy-loaded components
const InfoLanding = React.lazy(() => import('./components/Mainpage/InfoLanding'));
const Blog = React.lazy(() => import('./components/Mainpage/Blog'));
const BlogPostPage = React.lazy(() => import('./components/Mainpage/BlogPostPage'));
const Help = React.lazy(() => import('./components/Mainpage/Help'));
const Contact = React.lazy(() => import('./components/Mainpage/Contact'));
const EmailSignIn = React.lazy(() => import('./components/Mainpage/EmailSignIn'));
const Pricing = React.lazy(() => import('./components/Mainpage/Pricing'));
const NotFound = React.lazy(() => import('./components/Mainpage/NotFound'));
const ApiDocumentation = React.lazy(() => import('./components/Mainpage/ApiDocumentation'));
const UseCasePage = React.lazy(() => import('./components/Mainpage/UseCasePage'));

const Terms = React.lazy(() => import('./components/Mainpage/Terms'));
const Privacy = React.lazy(() => import('./components/Mainpage/Privacy'));
const Layout = React.lazy(() => import('./components/Mainpage/Layout'));
const LandingLayout = React.lazy(() => import('./components/Mainpage/LandingLayout'));
const WorkspaceProviders = React.lazy(() => import('./components/WorkspaceProviders'));
const Dashboard = React.lazy(() => import('./components/Mainpage/Dashboard'));
const AdminAccessGate = React.lazy(() => import('./components/Admin/AdminAccessGate'));
const EditorWrapper = React.lazy(() => import('./components/VideoEditor/EditorWrapper'));
const Projects = React.lazy(() => import('./components/Projects/Projects'));
const ProjectWindow = React.lazy(() => import('./components/Projects/ProjectWindow'));
const CuttingBoardStart = React.lazy(() => import('./components/Projects/CuttingBoardStart'));
const Assets = React.lazy(() => import('./components/Assets/index'));

const ProfileOverview = React.lazy(() => import('./components/ProfileOverview/ProfileOverviewPage'));
const SocialConsole = React.lazy(() => import('./components/SocialConsole'));
const GenerativeStudio = React.lazy(() => import('./components/GenerativeStudio'));
const DesktopSignIn = React.lazy(() => import('./components/DesktopSignIn'));
const ConnectApp = React.lazy(() => import('./components/ConnectApp'));
const EmbedCutPlayer = React.lazy(() => import('./components/EmbedPlayer/EmbedCutPlayer'));

const LazyFallback: React.FC = () => (
  <div className="flex-1 flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b border-rule"></div>
  </div>
);

// Protected Route wrapper with Layout
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LazyFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <WorkspaceProviders>
      <Seo config={resolveSeo(location.pathname)} />
      <Layout>{children}</Layout>
    </WorkspaceProviders>
  );
};

const SocialRoute: React.FC = () => {
  const available = useSocialPublishingAvailable();
  if (available === null) return <LazyFallback />;
  if (!available) return <Navigate to="/dashboard" replace />;
  return (
    <div className="flex-1 overflow-y-auto">
      <SocialConsole />
    </div>
  );
};

const PublicRoute: React.FC<{
  path: string;
  faq?: SeoConfig['faq'];
  howTo?: SeoConfig['howTo'];
  children: React.ReactNode;
}> = ({ path, faq, howTo, children }) => (
  <>
    <Seo config={{ ...resolveSeo(path), faq, howTo }} />
    {children}
  </>
);

const AppRoutes: React.FC = () => {
  const { isLoading } = useAuth();
  const navigate = useNavigate();
  // A selected local File is input state only. No upload or durable work exists yet.
  const [landingVideo, setLandingVideo] = React.useState<File | null>(null);
  const [landingUploadRequested, setLandingUploadRequested] = React.useState(false);
  const continueFromLanding = React.useCallback((file: File | null) => {
    setLandingVideo(file);
    setLandingUploadRequested(!file);
    navigate('/dashboard');
  }, [navigate]);
  const acceptLandingVideo = React.useCallback(() => setLandingVideo(null), []);
  const acceptLandingUpload = React.useCallback(() => setLandingUploadRequested(false), []);
  const adminHost = typeof window !== 'undefined' && isAdminHost(window.location.hostname);

  if (adminHost) {
    if (isLoading) return <LazyFallback />;
    return (
      <React.Suspense fallback={<LazyFallback />}>
        <Routes>
          <Route path="*" element={<AdminAccessGate />} />
        </Routes>
      </React.Suspense>
    );
  }

  return (
    <React.Suspense fallback={<LazyFallback />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={
          <PublicRoute path="/">
            <LandingLayout>
              <InfoLanding onContinue={continueFromLanding} />
            </LandingLayout>
          </PublicRoute>
        } />
        <Route path="/blog" element={<PublicRoute path="/blog"><Blog /></PublicRoute>} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/help" element={<PublicRoute path="/help"><Help /></PublicRoute>} />
        <Route path="/contact" element={<PublicRoute path="/contact"><Contact /></PublicRoute>} />
        <Route path="/login" element={<PublicRoute path="/login"><EmailSignIn /></PublicRoute>} />
        <Route path="/pricing" element={<PublicRoute path="/pricing"><Pricing /></PublicRoute>} />
        <Route path="/terms" element={<PublicRoute path="/terms"><Terms /></PublicRoute>} />
        <Route path="/privacy" element={<PublicRoute path="/privacy"><Privacy /></PublicRoute>} />
        {/* Use-case pages. Each one is prerendered and sitemapped from its
            seo/siteMetadata.json entry; useCaseContent.test.ts keeps the two
            lists from drifting apart. */}
        {useCasePages.map((page) => (
          <Route key={page.slug} path={`/${page.slug}`} element={
            <PublicRoute
              path={`/${page.slug}`}
              faq={page.faq}
              howTo={{ name: page.stepsHeading, steps: page.steps }}
            >
              <LandingLayout>
                <UseCasePage content={page} onContinue={continueFromLanding} />
              </LandingLayout>
            </PublicRoute>
          } />
        ))}

        <Route path="/api" element={
          <PublicRoute path="/api">
            <LandingLayout><ApiDocumentation /></LandingLayout>
          </PublicRoute>
        } />

        {/* Protected routes - all wrapped with Layout via ProtectedRoute */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard
              initialVideoFile={landingVideo}
              onVideoFileAccepted={acceptLandingVideo}
              initialUploadRequested={landingUploadRequested}
              onUploadRequestAccepted={acceptLandingUpload}
            />
          </ProtectedRoute>
        } />

        <Route path="/reports/releases" element={
          <ProtectedRoute>
            <Navigate to="/projects" replace />
          </ProtectedRoute>
        } />

        <Route path="/projects" element={
          <ProtectedRoute>
            <Projects />
          </ProtectedRoute>
        } />

        <Route path="/projects/:releaseId" element={
          <ProtectedRoute>
            <ProjectWindow />
          </ProtectedRoute>
        } />

        <Route path="/cutting-board" element={
          <ProtectedRoute>
            <CuttingBoardStart />
          </ProtectedRoute>
        } />

        <Route path="/project/:projectId" element={
          <ProtectedRoute>
            <div className="flex-1 bg-transparent overflow-hidden">
              <EditorWrapper />
            </div>
          </ProtectedRoute>
        } />

        <Route path="/assets" element={
          <ProtectedRoute>
            <div className="flex-1 overflow-y-auto">
              <Assets />
            </div>
          </ProtectedRoute>
        } />

        <Route path="/profile" element={
          <ProtectedRoute>
            <div className="po-scroll-container flex-1 overflow-y-auto">
              <ProfileOverview />
            </div>
          </ProtectedRoute>
        } />

        <Route path="/social" element={
          <ProtectedRoute>
            <SocialRoute />
          </ProtectedRoute>
        } />

        {/* The Studio's mode is in the URL below /studio; the Studio reads it. */}
        <Route path="/studio/*" element={
          <ProtectedRoute>
            {STUDIO_ENABLED ? (
              <div className="flex-1 bg-transparent overflow-hidden">
                <GenerativeStudio />
              </div>
            ) : (
              <Navigate to="/dashboard" replace />
            )}
          </ProtectedRoute>
        } />

        <Route path="/publishing" element={<Navigate to="/social" replace />} />

        {/* Birdhouse desktop sign-in: signs in here itself, outside the workspace Layout. */}
        <Route path="/desktop/sign-in" element={
          <><Seo config={resolveSeo('/desktop/sign-in')} /><DesktopSignIn /></>
        } />
        <Route path="/desktop/sign-in/done" element={
          <><Seo config={resolveSeo('/desktop/sign-in/done')} /><DesktopSignIn finished /></>
        } />
        {/* Consent for an app (ChatGPT, Claude) connecting to the Agent MCP over OAuth. */}
        <Route path="/connect" element={
          <><Seo config={resolveSeo('/connect')} /><ConnectApp /></>
        } />
        {/* A Cut played from a player link, framed by an app such as a chat; no sign-in. */}
        <Route path="/embed/cut/:cutId" element={
          <><Seo config={resolveSeo('/embed')} /><EmbedCutPlayer /></>
        } />


        <Route path="*" element={<><Seo config={resolveSeo('/404')} /><NotFound /></>} />
      </Routes>
    </React.Suspense>
  );
};

export default AppRoutes;
